/**
 * DS-internal hook : l'heure, relue à chaque changement de minute — et seulement quand on en a besoin.
 *
 * Rend l'instant courant en millisecondes. Tant que `enabled` est vrai, un minuteur se cale sur la
 * prochaine minute pleine puis se réarme minute après minute : un décompte en minutes change donc
 * au moment où la minute change, pas jusqu'à 59 s plus tard. `enabled` faux, rien ne tourne ; au
 * démontage ou quand `enabled` retombe, le minuteur en cours est libéré.
 */
import { useEffect, useState } from "react"

const MINUTE_MS = 60_000

/** Deux instants de la même minute pleine : un décompte en minutes ne les distingue pas. */
function sameMinute(a: number, b: number): boolean {
    return Math.floor(a / MINUTE_MS) === Math.floor(b / MINUTE_MS)
}

export function useMinuteClock(enabled: boolean): number {
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        if (!enabled) return undefined

        let timer: ReturnType<typeof setTimeout> | undefined
        const arm = () => {
            timer = setTimeout(
                () => {
                    setNow(Date.now())
                    arm()
                },
                MINUTE_MS - (Date.now() % MINUTE_MS)
            )
        }
        // Une carte remontée après une absence ne doit pas afficher l'heure de son premier rendu ;
        // au premier montage, la minute est encore celle du rendu, et React ignore l'état inchangé.
        const current = Date.now()
        setNow((previous) => (sameMinute(previous, current) ? previous : current))
        arm()

        return () => {
            if (timer != null) clearTimeout(timer)
        }
    }, [enabled])

    return now
}
